import {
  MAX_LINES,
  analyticsPayload,
  createLine,
  normalizeQuantity,
  serializeLines,
} from './annur-wholesale-model.js?v=20260909-email-structure-v1';
import { AnnurWholesaleCatalog } from './annur-wholesale-catalog.js?v=20260904-category-browser-v3';

class AnnurWholesaleRequest {
  constructor(root) {
    this.root = root;
    this.form = root.querySelector('form');
    this.linesContainer = root.querySelector('[data-lines]');
    this.summary = root.querySelector('[data-lines-summary]');
    this.referenceInput = root.querySelector('[data-request-reference]');
    this.lineCountInput = root.querySelector('[data-request-line-count]');
    this.unitCountInput = root.querySelector('[data-request-unit-count]');
    this.status = root.querySelector('[data-status]');
    this.steps = [...root.querySelectorAll('[data-wholesale-step]')];
    this.stepLabel = root.querySelector('[data-step-label]');
    this.progressBar = root.querySelector('[data-progress-bar]');
    this.review = root.querySelector('[data-wholesale-review]');
    this.currentStep = 0;
    this.lines = [];
    this.reference = this.createReference();
    this.referenceInput.value = this.reference;
    this.syncRequestMetrics();
    this.root.classList.add('is-enhanced');
    this.catalog = new AnnurWholesaleCatalog(root, {
      addLine: (line) => this.addLine(line),
      setStatus: (message) => this.setStatus(message),
    });
    this.bind();
    this.showStep(0, false);
    this.publishSuccess();
  }

  bind() {
    this.root.addEventListener('input', () => this.publishStarted(), { once: true });
    this.linesContainer.addEventListener('input', (event) => this.updateQuantity(event));
    this.linesContainer.addEventListener('click', (event) => this.removeLine(event));
    this.root.addEventListener('click', (event) => this.navigate(event));
    this.form.addEventListener('submit', () => this.beforeSubmit());
  }

  navigate(event) {
    const next = event.target.closest('[data-step-next]');
    const back = event.target.closest('[data-step-back]');
    if (!next && !back) return;
    if (next && !this.validateStep()) return;
    this.showStep(this.currentStep + (next ? 1 : -1));
  }

  validateStep() {
    const invalid = [...this.steps[this.currentStep].querySelectorAll('input, textarea, select')].find(
      (field) => !field.checkValidity(),
    );
    if (!invalid) return true;
    invalid.reportValidity();
    invalid.focus();
    return false;
  }

  showStep(index, shouldFocus = true) {
    this.currentStep = Math.max(0, Math.min(index, this.steps.length - 1));
    this.steps.forEach((step, stepIndex) => {
      step.hidden = stepIndex !== this.currentStep;
    });
    this.stepLabel.textContent = `Paso ${this.currentStep + 1} de ${this.steps.length}`;
    this.progressBar.style.width = `${((this.currentStep + 1) / this.steps.length) * 100}%`;
    if (this.currentStep === this.steps.length - 1) this.renderReview();
    if (!shouldFocus) return;
    this.steps[this.currentStep].querySelector('input, textarea, button')?.focus({ preventScroll: true });
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.steps[this.currentStep].scrollIntoView({ behavior: reducedMotion ? 'auto' : 'smooth', block: 'nearest' });
  }

  renderReview() {
    const company = this.form.elements.namedItem('contact[Empresa]')?.value.trim();
    const country = this.form.elements.namedItem('contact[País]')?.value.trim();
    const contact = this.form.elements.namedItem('contact[Contacto]')?.value.trim();
    const email = this.form.elements.namedItem('contact[email]')?.value.trim();
    const products = this.summary.value.trim();
    this.review.textContent = `Empresa: ${company}\nPaís: ${country}\nContacto: ${contact}\nEmail: ${email}\n\nProductos:\n${products}`;
  }

  addLine(line) {
    const existing = this.lines.find((item) => item.variantId === line.variantId);
    if (existing) existing.quantity = normalizeQuantity(existing.quantity + line.quantity);
    else if (this.lines.length < MAX_LINES) this.lines.push(line);
    else {
      this.setStatus(`Se permite un máximo de ${MAX_LINES} líneas.`);
      return;
    }
    this.renderLines();
    this.setStatus('Línea añadida.');
  }

  updateQuantity(event) {
    const index = Number.parseInt(event.target.dataset.quantityIndex, 10);
    if (!Number.isInteger(index)) return;
    this.lines[index].quantity = normalizeQuantity(event.target.value);
    event.target.value = String(this.lines[index].quantity);
    this.syncSummary();
  }

  removeLine(event) {
    const index = Number.parseInt(event.target.closest('[data-remove-index]')?.dataset.removeIndex, 10);
    if (!Number.isInteger(index)) return;
    this.lines.splice(index, 1);
    this.renderLines();
    this.setStatus('Línea eliminada.');
  }

  renderLines() {
    const fragment = document.createDocumentFragment();
    this.lines.forEach((line, index) => {
      const row = document.createElement('div');
      row.className = 'annur-wholesale__line';
      const description = document.createElement('p');
      description.textContent = `${line.product} — ${line.options || 'opción única'}${
        line.sku ? ` — SKU ${line.sku}` : ''
      }`;
      const quantity = document.createElement('input');
      quantity.type = 'number';
      quantity.min = '1';
      quantity.max = '999';
      quantity.value = String(line.quantity);
      quantity.dataset.quantityIndex = String(index);
      quantity.setAttribute('aria-label', `Cantidad para ${line.product}`);
      const remove = document.createElement('button');
      remove.type = 'button';
      remove.className = 'button button--secondary';
      remove.dataset.removeIndex = String(index);
      remove.textContent = 'Eliminar';
      row.append(description, quantity, remove);
      fragment.append(row);
    });
    this.linesContainer.replaceChildren(fragment);
    this.syncSummary();
  }

  syncSummary() {
    if (this.lines.length > 0) this.summary.value = serializeLines(this.reference, this.lines);
    this.syncRequestMetrics();
  }

  syncRequestMetrics() {
    const metrics = analyticsPayload(this.lines);
    const hasCatalogLines = metrics.line_count > 0;
    if (this.lineCountInput) {
      this.lineCountInput.value = hasCatalogLines ? String(metrics.line_count) : 'Detalle manual';
    }
    if (this.unitCountInput) {
      this.unitCountInput.value = hasCatalogLines ? String(metrics.total_quantity) : 'No calculado';
    }
  }

  beforeSubmit() {
    if (this.lines.length > 0) this.syncSummary();
    else this.syncRequestMetrics();
    sessionStorage.setItem('annurWholesaleSubmission', JSON.stringify(analyticsPayload(this.lines)));
  }

  publishStarted() {
    window.AnnurAnalytics?.publish('annur:wholesale_started', analyticsPayload(this.lines));
  }

  publishSuccess() {
    if (!this.root.querySelector('[data-success]')) return;
    const stored = sessionStorage.getItem('annurWholesaleSubmission');
    if (!stored) return;
    window.AnnurAnalytics?.publish('annur:wholesale_submitted', JSON.parse(stored));
    sessionStorage.removeItem('annurWholesaleSubmission');
  }

  setStatus(message) {
    this.status.textContent = message;
  }

  createReference() {
    if (window.crypto?.randomUUID) return `ANNUR-${window.crypto.randomUUID()}`;
    return `ANNUR-${Date.now().toString(36).toUpperCase()}`;
  }
}

document.querySelectorAll('[data-annur-wholesale]').forEach((root) => new AnnurWholesaleRequest(root));
